import {
  DueDiligenceAnswerType,
  DueDiligenceStatusColor,
  DueDiligenceEvaluationMode,
} from 'shared/types/due-diligence';
import type { QuestionItem } from 'screens/settings/DueDiligenceCategoryEditor/types';

export interface QuestionTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'compliance' | 'documents' | 'safety' | 'general';
  createQuestion: () => QuestionItem;
}

/**
 * Template category definitions
 */
export const TEMPLATE_CATEGORIES = [
  { id: 'compliance', label: 'Compliance & Regelgeving' },
  { id: 'documents', label: 'Documenten' },
  { id: 'safety', label: 'Veiligheid' },
  { id: 'general', label: 'Algemeen' },
] as const;

/**
 * Pre-built question templates for Due Diligence category editor
 * All templates create questions in Dutch with sensible defaults
 */
export const QUESTION_TEMPLATES: QuestionTemplate[] = [
  {
    id: 'epc_certificaat',
    name: 'EPC Certificaat',
    description: 'Controle op geldigheid van het EPC certificaat',
    icon: 'Certificate',
    category: 'compliance',
    createQuestion: (): QuestionItem => ({
      id: Date.now(),
      fieldName: 'epc_certificaat',
      displayName: 'EPC Certificaat',
      helpText: 'Controle op de geldigheid en aanwezigheid van het EPC (Energieprestatiecertificaat)',
      displayOrder: 0,
      active: true,
      answerType: DueDiligenceAnswerType.OPTIONS,
      evaluationMode: DueDiligenceEvaluationMode.MANUAL,
      documentLabelIds: [],
      answerOptions: [
        {
          value: 'geldig',
          label: 'Geldig',
          statusColor: DueDiligenceStatusColor.GREEN,
          tooltipLabel: 'EPC certificaat is geldig',
          isDefault: true,
          displayOrder: 0,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'bijna_verlopen',
          label: 'Bijna verlopen',
          statusColor: DueDiligenceStatusColor.ORANGE,
          tooltipLabel: 'EPC certificaat verloopt binnen 6 maanden',
          isDefault: false,
          displayOrder: 1,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'verlopen',
          label: 'Verlopen',
          statusColor: DueDiligenceStatusColor.RED,
          tooltipLabel: 'EPC certificaat is verlopen',
          isDefault: false,
          displayOrder: 2,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'ontbreekt',
          label: 'Ontbreekt',
          statusColor: DueDiligenceStatusColor.RED,
          tooltipLabel: 'Geen EPC certificaat gevonden',
          isDefault: false,
          displayOrder: 3,
          active: true,
          conditionRoot: null,
        },
      ],
      extractionValueType: null,
      extractionPrompt: null,
      extractionLabel: null,
    }),
  },

  {
    id: 'brandverzekering',
    name: 'Brandverzekering',
    description: 'Controle op actieve brandverzekering',
    icon: 'Fire',
    category: 'compliance',
    createQuestion: (): QuestionItem => ({
      id: Date.now(),
      fieldName: 'brandverzekering',
      displayName: 'Brandverzekering',
      helpText: 'Controle op de status en geldigheid van de brandverzekeringspolis',
      displayOrder: 0,
      active: true,
      answerType: DueDiligenceAnswerType.OPTIONS,
      evaluationMode: DueDiligenceEvaluationMode.MANUAL,
      documentLabelIds: [],
      answerOptions: [
        {
          value: 'actief',
          label: 'Actief',
          statusColor: DueDiligenceStatusColor.GREEN,
          tooltipLabel: 'Brandverzekering is actief',
          isDefault: true,
          displayOrder: 0,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'bijna_verlopen',
          label: 'Bijna verlopen',
          statusColor: DueDiligenceStatusColor.ORANGE,
          tooltipLabel: 'Brandverzekering verloopt binnenkort',
          isDefault: false,
          displayOrder: 1,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'verlopen',
          label: 'Verlopen',
          statusColor: DueDiligenceStatusColor.RED,
          tooltipLabel: 'Brandverzekering is verlopen',
          isDefault: false,
          displayOrder: 2,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'geen_polis',
          label: 'Geen polis',
          statusColor: DueDiligenceStatusColor.RED,
          tooltipLabel: 'Geen brandverzekeringspolis gevonden',
          isDefault: false,
          displayOrder: 3,
          active: true,
          conditionRoot: null,
        },
      ],
      extractionValueType: null,
      extractionPrompt: null,
      extractionLabel: null,
    }),
  },

  {
    id: 'keuringsattest',
    name: 'Keuringsattest',
    description: 'Controle op keuringsattest (gas, elektriciteit, lift, etc.)',
    icon: 'CheckCircle',
    category: 'safety',
    createQuestion: (): QuestionItem => ({
      id: Date.now(),
      fieldName: 'keuringsattest',
      displayName: 'Keuringsattest',
      helpText: 'Controle op aanwezigheid en conformiteit van keuringsattesten voor gas, elektriciteit, lift, etc.',
      displayOrder: 0,
      active: true,
      answerType: DueDiligenceAnswerType.OPTIONS,
      evaluationMode: DueDiligenceEvaluationMode.MANUAL,
      documentLabelIds: [],
      answerOptions: [
        {
          value: 'conform',
          label: 'Conform',
          statusColor: DueDiligenceStatusColor.GREEN,
          tooltipLabel: 'Keuring conform',
          isDefault: true,
          displayOrder: 0,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'niet_conform',
          label: 'Niet conform',
          statusColor: DueDiligenceStatusColor.RED,
          tooltipLabel: 'Keuring niet conform',
          isDefault: false,
          displayOrder: 1,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'verlopen',
          label: 'Verlopen',
          statusColor: DueDiligenceStatusColor.ORANGE,
          tooltipLabel: 'Keuringsattest is verlopen',
          isDefault: false,
          displayOrder: 2,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'ontbreekt',
          label: 'Ontbreekt',
          statusColor: DueDiligenceStatusColor.RED,
          tooltipLabel: 'Geen keuringsattest gevonden',
          isDefault: false,
          displayOrder: 3,
          active: true,
          conditionRoot: null,
        },
      ],
      extractionValueType: null,
      extractionPrompt: null,
      extractionLabel: null,
    }),
  },

  {
    id: 'vergunning',
    name: 'Vergunning',
    description: 'Controle op status van vergunningsaanvraag',
    icon: 'Stamp',
    category: 'compliance',
    createQuestion: (): QuestionItem => ({
      id: Date.now(),
      fieldName: 'vergunning',
      displayName: 'Vergunning',
      helpText: 'Controle op de status van vergunningsaanvragen en verleende vergunningen',
      displayOrder: 0,
      active: true,
      answerType: DueDiligenceAnswerType.OPTIONS,
      evaluationMode: DueDiligenceEvaluationMode.MANUAL,
      documentLabelIds: [],
      answerOptions: [
        {
          value: 'vergund',
          label: 'Vergund',
          statusColor: DueDiligenceStatusColor.GREEN,
          tooltipLabel: 'Vergunning verleend',
          isDefault: true,
          displayOrder: 0,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'geweigerd',
          label: 'Geweigerd',
          statusColor: DueDiligenceStatusColor.RED,
          tooltipLabel: 'Vergunning geweigerd',
          isDefault: false,
          displayOrder: 1,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'lopend',
          label: 'Lopend',
          statusColor: DueDiligenceStatusColor.ORANGE,
          tooltipLabel: 'Vergunningsaanvraag in behandeling',
          isDefault: false,
          displayOrder: 2,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'geen_aanvraag',
          label: 'Geen aanvraag',
          statusColor: DueDiligenceStatusColor.TRANSPARENT,
          tooltipLabel: 'Geen vergunningsaanvraag ingediend',
          isDefault: false,
          displayOrder: 3,
          active: true,
          conditionRoot: null,
        },
      ],
      extractionValueType: null,
      extractionPrompt: null,
      extractionLabel: null,
    }),
  },

  {
    id: 'document_aanwezig',
    name: 'Document aanwezig',
    description: 'Eenvoudige controle of een document aanwezig is',
    icon: 'File',
    category: 'documents',
    createQuestion: (): QuestionItem => ({
      id: Date.now(),
      fieldName: 'document_aanwezig',
      displayName: 'Document aanwezig',
      helpText: 'Controle op de aanwezigheid van een specifiek document',
      displayOrder: 0,
      active: true,
      answerType: DueDiligenceAnswerType.DOCUMENT_EXISTS,
      evaluationMode: DueDiligenceEvaluationMode.FILE_BASED,
      documentLabelIds: [],
      answerOptions: [
        {
          value: 'aanwezig',
          label: 'Aanwezig',
          statusColor: DueDiligenceStatusColor.GREEN,
          tooltipLabel: 'Document is aanwezig',
          isDefault: true,
          displayOrder: 0,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'ontbreekt',
          label: 'Ontbreekt',
          statusColor: DueDiligenceStatusColor.RED,
          tooltipLabel: 'Document ontbreekt',
          isDefault: false,
          displayOrder: 1,
          active: true,
          conditionRoot: null,
        },
      ],
      extractionValueType: null,
      extractionPrompt: null,
      extractionLabel: null,
    }),
  },

  {
    id: 'geldigheidscontrole',
    name: 'Geldigheidscontrole',
    description: 'Algemene controle op geldigheid van een item',
    icon: 'SealCheck',
    category: 'general',
    createQuestion: (): QuestionItem => ({
      id: Date.now(),
      fieldName: 'geldigheidscontrole',
      displayName: 'Geldigheidscontrole',
      helpText: 'Algemene controle op de geldigheid van een item of document',
      displayOrder: 0,
      active: true,
      answerType: DueDiligenceAnswerType.OPTIONS,
      evaluationMode: DueDiligenceEvaluationMode.MANUAL,
      documentLabelIds: [],
      answerOptions: [
        {
          value: 'geldig',
          label: 'Geldig',
          statusColor: DueDiligenceStatusColor.GREEN,
          tooltipLabel: 'Item is geldig',
          isDefault: true,
          displayOrder: 0,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'niet_geldig',
          label: 'Niet geldig',
          statusColor: DueDiligenceStatusColor.RED,
          tooltipLabel: 'Item is niet geldig',
          isDefault: false,
          displayOrder: 1,
          active: true,
          conditionRoot: null,
        },
        {
          value: 'onbekend',
          label: 'Onbekend',
          statusColor: DueDiligenceStatusColor.ORANGE,
          tooltipLabel: 'Geldigheid onbekend',
          isDefault: false,
          displayOrder: 2,
          active: true,
          conditionRoot: null,
        },
      ],
      extractionValueType: null,
      extractionPrompt: null,
      extractionLabel: null,
    }),
  },
];

/**
 * Get a template by its ID
 * @param id - The template ID to search for
 * @returns The template if found, undefined otherwise
 */
export function getTemplateById(id: string): QuestionTemplate | undefined {
  return QUESTION_TEMPLATES.find((template) => template.id === id);
}

/**
 * Get all templates for a specific category
 * @param category - The category to filter by
 * @returns Array of templates in that category
 */
export function getTemplatesByCategory(
  category: 'compliance' | 'documents' | 'safety' | 'general'
): QuestionTemplate[] {
  return QUESTION_TEMPLATES.filter((template) => template.category === category);
}

/**
 * Get category label by ID
 * @param categoryId - The category ID
 * @returns The category label in Dutch
 */
export function getCategoryLabel(categoryId: string): string {
  const category = TEMPLATE_CATEGORIES.find((cat) => cat.id === categoryId);
  return category?.label || categoryId;
}
